import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Instagram, CalendarPlus, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const fullUrl = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function PublicTeamGallery() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/public/team/${slug}`);
        setData(d);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="p-8 max-w-md text-center">Empresa não encontrada.</Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 py-10 px-4">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center justify-center gap-2">
            <Building2 className="w-3 h-3" /> {data.tenant.name}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Nossa equipe</h1>
          <p className="text-sm text-muted-foreground mt-2">Escolha o profissional para agendar seu atendimento.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((m) => (
            <Card
              key={m.id}
              data-testid={`pub-team-${m.id}`}
              onClick={() => setSelected(m)}
              className="p-5 rounded-2xl cursor-pointer transition hover:shadow-xl hover:-translate-y-1"
            >
              <div className="flex flex-col items-center text-center">
                {m.avatar_url ? (
                  <img src={fullUrl(m.avatar_url)} alt={m.name} className="w-24 h-24 rounded-full object-cover border-4 border-primary mb-3" onError={(e) => e.target.style.display = "none"} />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center font-bold text-2xl mb-3">{m.name?.[0] || "?"}</div>
                )}
                <h3 className="font-display font-bold text-lg">{m.name}</h3>
                {m.specialties?.length > 0 && (
                  <div className="flex flex-wrap gap-1 justify-center mt-2">
                    {m.specialties.slice(0, 3).map((s, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px]">{s}</Badge>
                    ))}
                  </div>
                )}
                {m.bio && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{m.bio}</p>}
              </div>
            </Card>
          ))}
        </div>

        {data.items.length === 0 && (
          <Card className="p-10 text-center text-muted-foreground rounded-2xl">
            Nenhum profissional disponível no momento.
          </Card>
        )}

        {selected && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
            <Card className="max-w-2xl w-full rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row gap-5 items-start">
                  {selected.avatar_url && <img src={fullUrl(selected.avatar_url)} alt={selected.name} className="w-28 h-28 rounded-full object-cover border-4 border-primary shrink-0 mx-auto sm:mx-0" />}
                  <div className="flex-1 min-w-0 text-center sm:text-left">
                    <h2 className="font-display text-3xl font-bold">{selected.name}</h2>
                    {selected.bio && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{selected.bio}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-3 justify-center sm:justify-start">
                      {selected.specialties.map((s, i) => <Badge key={i} variant="secondary">{s}</Badge>)}
                    </div>
                    {selected.instagram && (
                      <a href={`https://instagram.com/${selected.instagram}`} target="_blank" rel="noopener noreferrer" className="text-xs underline mt-3 inline-flex items-center gap-1">
                        <Instagram className="w-3 h-3" /> @{selected.instagram}
                      </a>
                    )}
                  </div>
                </div>

                {selected.portfolio_images?.length > 0 && (
                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Portfólio</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {selected.portfolio_images.map((url, i) => (
                        url.toLowerCase().endsWith(".pdf") ? (
                          <a key={i} href={fullUrl(url)} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg border bg-muted flex items-center justify-center text-xs underline">📄 PDF {i+1}</a>
                        ) : (
                          <a key={i} href={fullUrl(url)} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden border">
                            <img src={fullUrl(url)} alt="" className="w-full h-full object-cover" onError={(e) => e.target.parentElement.innerHTML="<div class='text-xs p-2'>(imagem inválida)</div>"} />
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setSelected(null)} className="w-full sm:w-auto">Fechar</Button>
                  <Button data-testid="pub-book" className="w-full sm:flex-1">
                    <CalendarPlus className="w-4 h-4 mr-2" /> Agendar com {selected.name.split(" ")[0]}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
